import { Accordion, Box, Divider, Popover } from '@eatclub-apps/ec-component-library';
import { useTheme } from '@material-ui/core';
import { useRollbar } from '@rollbar/react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import validator from 'validator';
import { ReactComponent as IconMinus } from '../../../assets/icons/minus.svg';
import { ReactComponent as IconPlus } from '../../../assets/icons/plus.svg';
import { Button } from '../../../components/Button';
import { IconButton } from '../../../components/IconButton';
import { Spacer } from '../../../components/Spacer';
import { TextField } from '../../../components/TextField';
import { Typography } from '../../../components/Typography';
import {
  batchUpdateBookingAction,
  updateBookingAction,
  updateBookingStepAction,
} from '../../../data/actions/bookingAction';
import { fetchBookingTermsAction } from '../../../data/actions/bookingTermsAction';
import { holdExperiencesAction } from '../../../data/actions/experienceAction';
import { addMessageAction } from '../../../data/actions/messagesAction';
import { holdBookingAction } from '../../../data/actions/obeeBookingAction';
import {
  fetchPaymentSettingsAction,
  fetchPaymentSettingsByIdAction,
} from '../../../data/actions/paymentSettingsAction';
import { bookingKeys, bookingPropTypes, bookingSteps } from '../../../data/models/Booking';
import { bookingTermsPropTypes } from '../../../data/models/BookingTerms';
import { addonGroupsPropTypes } from '../../../data/models/Experience';
import { restaurantPropTypes } from '../../../data/models/Restaurant';
import { PageLayout } from '../../../layouts/PageLayout';
import { WidgetLayout } from '../../../layouts/WidgetLayout';
import { trackEvent } from '../../../utils/analytics';
import { getAddonGroupsForBooking, getExperienceTerms } from '../../../utils/bookingHelpers';
import { COUNTRY_CODES, ERROR_MESSAGES, isObeeLogic } from '../../../utils/constants';
import { deepEqual, isEmpty, range, trim } from '../../../utils/helpers';
import { useIsMobile } from '../../../utils/hooks';
import { safeParse } from '../../../utils/stringHelpers';
import { getContrastTextColor } from '../../../utils/themeHelpers';
import { DietaryRequirements } from './DietaryRequirements';

const Details = ({
  booking,
  bookingTerms,
  restaurant,
  fetchBookingTerms,
  fetchPaymentSettings,
  fetchPaymentSettingsById,
  updateBooking,
  updateBookingStep,
  batchUpdateBooking,
  addonGroups,
  addMessage,
}) => {
  const rollbar = useRollbar();
  const theme = useTheme();
  const isMobile = useIsMobile();

  const [firstErrors, setFirstErrors] = useState(null);
  const [lastErrors, setLastErrors] = useState(null);
  const [mobileErrors, setMobileErrors] = useState(null);
  const [emailErrors, setEmailErrors] = useState(null);
  const [dietaryErrors, setDietaryErrors] = useState([]);
  const [firstDietaryError, setFirstDietaryError] = useState(0);
  const [countryPopoverAnchorEl, setCountryPopoverAnchorEl] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState({ name: 'Australia', label: '+61' });
  const [searchQuery, setSearchQuery] = useState('');

  const highchairCap = 4; // TODO: don't forget about this, should the limit of highchairs be a setting or dependant on guest/kid size?

  const addonGroupsForBooking = getAddonGroupsForBooking(booking, addonGroups);

  const dietariesRequireActualNames = safeParse(restaurant.data?.bookingWidgetSettings)
    ?.widgetSettings?.settingsDetails?.dietariesRequireActualNames;

  // Check if any selected experience has the 'dont_require_dietaries' flag
  const hideDietaryRequirements =
    booking?.selectedExperiences?.some((item) =>
      item?.experience?.optionalBehaviour?.includes('dont_require_dietaries'),
    ) ?? false;

  // Show dietary requirements if enabled by restaurant AND not explicitly disabled by experience
  const showDietaryRequirements =
    restaurant?.data?.showDietaryRequirements && !hideDietaryRequirements;

  // Set guest dietaries on initial load
  useEffect(() => {
    const loadedDietaries = booking?.dietaryRequirements?.map((requirement, index) => ({
      ...requirement,
      index,
    }));
    const newDietaries = range(0, booking.guests - 1).map((guestIndex, index) => ({
      name: loadedDietaries?.[guestIndex]?.name || null,
      dietaries: loadedDietaries?.[guestIndex]?.dietaries || [], // list of strings for chosen dietary options
      dietaryNotes: '', // custom notes from the guest
      noDietaries: loadedDietaries?.[guestIndex]?.noDietaries ?? false,
      index,
    }));

    updateBooking('dietaryRequirements', newDietaries);
  }, [booking.guests]);

  useEffect(() => {
    if (booking?.mobilePrefix) {
      const country = COUNTRY_CODES?.find((c) => c.label === booking.mobilePrefix);
      setSelectedCountry(country ?? { name: 'Australia', label: '+61' });
    }
  }, []);

  const getInvalidDietaries = (requirements) => {
    if (requirements?.length < booking?.guests) {
      return requirements;
    }

    return requirements
      .map((dietaryRequirement, index) => ({
        ...dietaryRequirement,
        index,
      }))
      .filter((dietaryRequirement) => {
        // If the restaurant does not require actual names,then we only need to check if the dietaries are empty
        if (dietariesRequireActualNames === false) {
          return isEmpty(dietaryRequirement?.dietaries) && !dietaryRequirement?.noDietaries;
        }

        // If dietaryTagsMandatory is true, and dietariesRequireActualNames is true/undefined/null
        // Every guest on the booking must have a name, and either dietaries selected or set to not show them
        return (
          (dietaryRequirement?.index !== 0 && isEmpty(trim(dietaryRequirement?.name))) ||
          (isEmpty(dietaryRequirement?.dietaries) && !dietaryRequirement?.noDietaries)
        );
      });
  };

  const submit = () => {
    trackEvent('Navigation', 'Updating Step', 'Next Button');
    let valid = true;

    const trimFirstName = booking.firstName?.trim();
    if (isEmpty(trimFirstName)) {
      valid = false;
      rollbar.warning(`Invalid first name: ${trimFirstName}`);
      setFirstErrors(ERROR_MESSAGES.requiredField);
    } else {
      setFirstErrors(null);
      updateBooking(bookingKeys.firstName, trimFirstName);
    }

    const trimLastName = booking.lastName?.trim();
    if (isEmpty(trimLastName)) {
      valid = false;
      rollbar.warning(`Invalid last name: ${trimLastName}`);
      setLastErrors(ERROR_MESSAGES.requiredField);
    } else {
      setLastErrors(null);
      updateBooking(bookingKeys.lastName, trimLastName);
    }

    const trimMobile = booking.mobile?.trim();
    if (!validator.isMobilePhone(trimMobile || '')) {
      valid = false;
      rollbar.warning(`Invalid phone number: ${trimMobile}`);
      setMobileErrors(ERROR_MESSAGES.validMobile);
    } else {
      setMobileErrors(null);
      updateBooking(bookingKeys.mobile, trimMobile);
    }

    const trimEmail = booking.email?.trim();
    if (!validator.isEmail(trimEmail || '')) {
      valid = false;
      rollbar.warning(`Invalid email: ${trimEmail}`);
      setEmailErrors(ERROR_MESSAGES.validEmail);
    } else {
      setEmailErrors(null);
      updateBooking(bookingKeys.email, trimEmail);
    }

    // Validate dietary requirements if mandatory
    if (restaurant.data?.dietaryTagsMandatory && !hideDietaryRequirements) {
      const invalidDietaries = getInvalidDietaries(booking?.dietaryRequirements);

      if (!isEmpty(invalidDietaries)) {
        valid = false;
        rollbar.warning(
          `Invalid dietary requirements: ${JSON.stringify(booking?.dietaryRequirements)}`,
        );
        addMessage(
          `VALIDATE_DIETARIES_${new Date().getTime()}`,
          'Please provide dietaries for each guest',
          'error',
        );

        setDietaryErrors(invalidDietaries);
        setFirstDietaryError(invalidDietaries?.[0]?.index);
      }
    }

    if (valid) {
      if (!isEmpty(addonGroupsForBooking.afterDetails)) {
        updateBookingStep(bookingSteps.addons, 0);
      } else {
        updateBookingStep(bookingSteps.review);
      }
    }
  };

  // If dietary requirements change, revalidate
  useEffect(() => {
    if (!isEmpty(dietaryErrors)) {
      const invalidDietaries = getInvalidDietaries(booking?.dietaryRequirements);
      setDietaryErrors(invalidDietaries);
    }
  }, [booking?.dietaryRequirements]);

  /**
   * Pre-fetch payment settings for next screen
   */
  useEffect(() => {
    if (!isObeeLogic) {
      return;
    }

    const { date, availabilitySuggestion, guests, selectedExperiences, addons } = booking;

    const areaId = availabilitySuggestion?.table?.areaId || booking?.areaId;

    if (!areaId) {
      // todo: confirm that if we should fetch payments for waitlist, theres a flow where joining the waitlist doesn't assign a table/area, areaId is required
      return;
    }

    const experienceCounts = (selectedExperiences ?? []).filter(
      (experienceCount) => experienceCount?.count > 0,
    );

    const addonCounts = Object.entries(addons)
      .filter(([_addonId, addon]) => addon?.count > 0)
      .map(([_addonId, addon]) => addon?.addon);

    // Find the first payment method on any of the experiences
    let paymentSettingId;

    if (!isEmpty(experienceCounts)) {
      paymentSettingId = (experienceCounts ?? []).find(
        (experienceCount) => experienceCount?.experience?.paymentSettingId !== null,
      )?.experience?.paymentSettingId;
    } else if (!isEmpty(addonCounts)) {
      // There are no experiences with the booking and there are add-ons with payments
      paymentSettingId = addonCounts.find(
        (addonCount) => addonCount?.includeInPayment && addonCount?.paymentSettingsId !== null,
      )?.paymentSettingsId;
    }

    // Experiences/generic addons override payment settings
    if (!isEmpty(paymentSettingId)) {
      fetchPaymentSettingsById(paymentSettingId);
    } else {
      fetchPaymentSettings({
        paymentSettings: {
          date,
          time: availabilitySuggestion?.time,
          size: guests,
          public: 1,
          areaId,
        },
        restId: restaurant.data.objectId,
      });
    }
  }, []);

  // Fetch booking terms for next screen
  useEffect(() => {
    // note: EatClub has the terms included in the restaurant object
    if (!isObeeLogic) {
      return;
    }

    // If we have custom terms, no need to fetch
    const customTerms = getExperienceTerms(booking);
    if (!isEmpty(customTerms)) {
      return;
    }

    const { date, availabilitySuggestion, guests } = booking;

    const areaId = availabilitySuggestion?.table?.areaId;

    const newVariables = {
      restId: restaurant.data.objectId,
      areaId,
      date,
      time: availabilitySuggestion?.time,
      size: guests,
    };

    if (deepEqual(newVariables, bookingTerms.lastUsedVariables)) {
      // the terms have been fetched
      return;
    }

    fetchBookingTerms(areaId, date, availabilitySuggestion?.time, guests);
  }, []);

  const specialRequests = {
    id: 'specialRequests',
    summary: 'Add special requests',
    defaultOpen: !isEmpty(booking?.specialRequirements),
    details: (
      <Box>
        <TextField
          // style={{ marginTop: '12px' }}
          onFocus={() => trackEvent('Booking', 'Updating Special Requests')}
          label='Special requests'
          multiline
          minRows={8}
          value={booking.specialRequirements || ''}
          onChange={(e) => updateBooking(bookingKeys.specialRequirements, e.target.value)}
          InputProps={{ disableUnderline: true }}
          style={{ minHeight: '150px' }}
        />
      </Box>
    ),
  };

  // Get the items to show in the accordion
  const accordionItems = showDietaryRequirements
    ? [
        {
          id: 'dietary',
          summary: `Add dietary requirements ${
            restaurant?.data?.dietaryTagsMandatory ? '- Required' : ''
          }`,
          defaultOpen:
            !isEmpty(booking?.dietaryRequirements) || restaurant?.data?.dietaryTagsMandatory,
          // subtitle:
          //   'Dietaries are required before the booking date. However, can be provided later.',
          details: (
            <DietaryRequirements
              mandatory={
                restaurant?.data?.dietaryTagsMandatory && dietariesRequireActualNames !== false // mandatory is true when dietaryTagsMandatory is true and the restaurant requires actual names(dietariesRequireActualNames is true/undefined)
              }
              dietaryTagsMandatory={restaurant?.data?.dietaryTagsMandatory}
              dietaryErrors={dietaryErrors}
              defaultTab={firstDietaryError ?? 0}
            />
          ),
        },
        specialRequests,
      ]
    : [specialRequests];

  const handleCountryClick = (event) => {
    setSearchQuery('');
    setCountryPopoverAnchorEl(event.currentTarget);
  };

  const handleCountrySelect = (country) => {
    updateBooking('mobilePrefix', country.label);
    setSelectedCountry(country);
    setCountryPopoverAnchorEl(null);
  };

  const handleClosePopover = () => {
    setCountryPopoverAnchorEl(null);
  };

  const filteredCountries = COUNTRY_CODES.filter((country) =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <PageLayout>
      <WidgetLayout showDetails>
        <Spacer direction='vertical' gap={40}>
          <Spacer direction='vertical' gap={24}>
            <Typography variant='h2'>Provide your booking details</Typography>
            <Spacer direction='vertical' gap={12}>
              <Spacer wrap={isMobile} gap={12}>
                <TextField
                  onFocus={() => trackEvent('Booking', 'Updating First Name')}
                  autoFocus={!booking.firstName}
                  required
                  error={!isEmpty(firstErrors)}
                  helperText={<Typography variant='caption'>{firstErrors}</Typography>}
                  label='First name'
                  value={booking.firstName || ''}
                  onChange={(e) => updateBooking(bookingKeys.firstName, e.target.value)}
                  InputProps={{ disableUnderline: true }}
                  onBlur={(e) => {
                    if (!isEmpty(firstErrors) && !isEmpty(e.target.value)) {
                      setFirstErrors(null);
                    }
                  }}
                  autoComplete='given-name'
                  name='firstName'
                />
                <TextField
                  onFocus={() => trackEvent('Booking', 'Updating Last Name')}
                  required
                  error={!isEmpty(lastErrors)}
                  helperText={<Typography variant='caption'>{lastErrors}</Typography>}
                  label='Last name'
                  value={booking.lastName || ''}
                  onChange={(e) => updateBooking(bookingKeys.lastName, e.target.value)}
                  InputProps={{ disableUnderline: true }}
                  onBlur={(e) => {
                    if (!isEmpty(lastErrors) && !isEmpty(e.target.value)) {
                      setLastErrors(null);
                    }
                  }}
                  autoComplete='family-name'
                  name='lastName'
                />
              </Spacer>
              <Box style={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant='text'
                  onClick={handleCountryClick}
                  showArrowDown
                  showActiveBorder
                  style={{
                    cursor: 'pointer',
                    border: 'none',
                    marginRight: '10px',
                    // padding: '10px 0',
                    color: theme.colors.secondaryTextColour,
                    width: '100px',
                    outline: `1px solid ${theme.colors.outlineColour}`,
                    height: '56px',
                  }}
                >
                  {selectedCountry.label}
                </Button>
                <Popover
                  isOpen={!!countryPopoverAnchorEl}
                  onClose={handleClosePopover}
                  anchorEl={countryPopoverAnchorEl}
                  showArrow
                  innerStyle={{
                    borderRadius: '8px',
                    padding: '0',
                    position: 'relative',
                    marginTop: '0',
                    // maxWidth: restaurant.data.premium ? !breakpointXsDown && '540px' : 'fit-content',
                    width: 'fit-content',
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <Spacer
                    gap={0}
                    direction='vertical'
                    style={{
                      fontFamily: theme.baseTypography.availabilityFontFamily,
                      padding: '8px',
                      maxHeight: '360px',
                    }}
                  >
                    <TextField
                      placeholder='Search countries...'
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{ disableUnderline: true }}
                      style={{ marginBottom: '8px', width: '100%' }}
                    />
                    {filteredCountries.map((country) => (
                      <Button
                        key={country.name}
                        variant='text'
                        onClick={() => handleCountrySelect(country)}
                        style={{
                          padding: '8px',
                          cursor: 'pointer',
                          color: theme.colors.darkMode
                            ? theme.colors.availabilityMainButtonTextColour
                            : theme.colors.mainTextColour,
                          width: '100%',
                          container: {
                            justifyContent: 'flex-start',
                          },
                        }}
                      >
                        <Box
                          style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}
                        >
                          {country.name} {country.label}
                        </Box>
                      </Button>
                    ))}
                  </Spacer>
                </Popover>
                <TextField
                  onFocus={() => trackEvent('Booking', 'Updating Mobile Number')}
                  required
                  error={!isEmpty(mobileErrors)}
                  helperText={<Typography variant='caption'>{mobileErrors}</Typography>}
                  label='Mobile'
                  value={booking.mobile || ''}
                  onChange={(e) => updateBooking(bookingKeys.mobile, e.target.value)}
                  type='tel'
                  InputProps={{
                    disableUnderline: true,
                    // startAdornment: <InputAdornment position='start'>+61</InputAdornment>,
                  }}
                  onBlur={(e) => {
                    if (!isEmpty(mobileErrors) && validator.isMobilePhone(e.target.value)) {
                      setMobileErrors(null);
                    }
                  }}
                  autoComplete='tel-national'
                  name='mobile'
                />
              </Box>
              <Box>
                <TextField
                  onFocus={() => trackEvent('Booking', 'Updating Email')}
                  required
                  error={!isEmpty(emailErrors)}
                  helperText={<Typography variant='caption'>{emailErrors}</Typography>}
                  label='Email'
                  value={booking.email || ''}
                  onChange={(e) => updateBooking(bookingKeys.email, e.target.value)}
                  type='email'
                  InputProps={{ disableUnderline: true }}
                  onBlur={(e) => {
                    if (!isEmpty(emailErrors) && validator.isEmail(e.target.value)) {
                      setEmailErrors(null);
                    }
                  }}
                  autoComplete='email'
                  name='email'
                />
                <Box style={{ padding: '4px 0 0 0', color: theme.colors.secondaryTextColour }}>
                  <Typography variant='caption'>For booking confirmation and reminders.</Typography>
                </Box>
              </Box>

              {restaurant.data?.showPostcodeField && (
                <Spacer gap={12} wrap={isMobile}>
                  {restaurant.data?.showPostcodeField && (
                    <TextField
                      // error={!isEmpty(emailErrors)}
                      // helperText={<Typography variant='caption'>{emailErrors}</Typography>}
                      label='Postcode'
                      value={booking?.postcode || ''}
                      onChange={(e) => updateBooking('postcode', e.target.value)}
                      type='text'
                      InputProps={{ disableUnderline: true }}
                      name='postcode'
                    />
                  )}
                </Spacer>
              )}
            </Spacer>

            {restaurant.data.showChildren && (
              <Spacer style={{ alignItems: 'center', justifyContent: 'space-between' }} wrap>
                <Typography
                  style={{
                    fontWeight: theme.fontWeights.medium,
                    color: theme.colors.secondaryTextColour,
                  }}
                >
                  Are any children on the booking?
                </Typography>
                <Spacer
                  centered
                  style={{
                    border: `1px solid ${theme.colors.outlineColour}`,
                    padding: '8px 12px',
                    borderRadius: '6px',
                    width: '132px',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography>{booking.kids === 0 ? 'None' : booking.kids}</Typography>
                  <Spacer>
                    <IconButton
                      disabled={booking.kids === 0}
                      variant='outlined'
                      onClick={() => {
                        trackEvent('Booking', 'Updating Children', 'Decrement');
                        const newKids = Math.max(booking.kids - 1, 0); // Default in case it's NaN
                        batchUpdateBooking({
                          [bookingKeys.kids]: newKids,
                          [bookingKeys.highchairs]: Math.min(newKids, booking.highchairs),
                        });
                      }}
                    >
                      <IconMinus />
                    </IconButton>
                    <IconButton
                      disabled={booking.kids >= booking.guests}
                      variant='outlined'
                      onClick={() => {
                        trackEvent('Booking', 'Updating Children', 'Increment');
                        if (Number.isNaN(booking.kids)) {
                          updateBooking(bookingKeys.kids, 1);
                        } else {
                          updateBooking(
                            bookingKeys.kids,
                            Math.min(booking.kids + 1, booking.guests),
                          );
                        }
                      }}
                    >
                      <IconPlus />
                    </IconButton>
                  </Spacer>
                </Spacer>
              </Spacer>
            )}

            {booking.kids > 0 && restaurant.data.showHighchairs && (
              <Spacer style={{ alignItems: 'center', justifyContent: 'space-between' }} wrap>
                <Typography
                  style={{
                    fontWeight: theme.fontWeights.medium,
                    color: theme.colors.secondaryTextColour,
                  }}
                >
                  Do you need any high chairs?
                </Typography>
                <Spacer
                  centered
                  style={{
                    border: `1px solid ${theme.colors.outlineColour}`,
                    padding: '8px 12px',
                    borderRadius: '6px',
                    width: '132px',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography>{booking.highchairs === 0 ? 'None' : booking.highchairs}</Typography>
                  <Spacer>
                    <IconButton
                      disabled={booking.highchairs === 0}
                      variant='outlined'
                      onClick={() => {
                        trackEvent('Booking', 'Updating Highchairs', 'Decrement');
                        updateBooking(bookingKeys.highchairs, Math.max(booking.highchairs - 1, 0));
                      }}
                    >
                      <IconMinus />
                    </IconButton>
                    <IconButton
                      disabled={
                        booking.highchairs >= highchairCap || booking.highchairs >= booking.kids
                      }
                      variant='outlined'
                      onClick={() => {
                        trackEvent('Booking', 'Updating Highchairs', 'Increment');
                        updateBooking(
                          bookingKeys.highchairs,
                          Math.min(booking.highchairs + 1, highchairCap, booking.kids),
                        );
                      }}
                    >
                      <IconPlus />
                    </IconButton>
                  </Spacer>
                </Spacer>
              </Spacer>
            )}
          </Spacer>
          <Box>
            {/* <Spacer direction='vertical' gap={20}> */}
            {/*   <Typography variant='h4' style={{ fontSize: '18px' }}> */}
            {/*     Additional information */}
            {/*   </Typography> */}
            <Divider color={theme.colors.dividerColor} />
            {/* </Spacer> */}
            <Accordion
              variant='plus'
              style={{
                summaryActive: {
                  fontWeight: theme.fontWeights.regular,
                  // color: theme.colors.darkMode ? theme.colors.white : theme.colors.charcoal,
                  color: theme.colors.mainTextColour,
                },
                itemSummary: {
                  padding: '0',
                  // color: theme.colors.darkMode ? theme.colors.white : theme.colors.charcoal,
                  color: theme.colors.mainTextColour,
                },
                itemHover: {
                  // color: theme.colors.darkMode ? theme.colors.white : theme.colors.black,
                  color: theme.colors.mainTextColour,
                },
                itemFocus: {
                  // color: theme.colors.darkMode ? theme.colors.white : theme.colors.black,
                  color: theme.colors.mainTextColour,
                },
                itemDescription: {
                  padding: '2px 2px 40px 30px',
                  color: theme.colors.mainTextColour,
                  // ...theme.baseTypography,
                },
                item: {
                  borderBottom: `1px solid ${theme.colors.dividerColor}`,
                },
              }}
              items={accordionItems}
              multiple
            />
          </Box>

          <Box style={{ display: 'flex' }}>
            <Button
              size='large'
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                submit();
              }}
              style={{ marginLeft: 'auto', minWidth: '144px' }}
              fullWidth={isMobile}
              color='primary'
            >
              Next
            </Button>
          </Box>
        </Spacer>
      </WidgetLayout>
    </PageLayout>
  );
};

Details.propTypes = {
  booking: bookingPropTypes.isRequired,
  bookingTerms: bookingTermsPropTypes.isRequired,
  restaurant: restaurantPropTypes.isRequired,
  fetchBookingTerms: PropTypes.func.isRequired,
  updateBooking: PropTypes.func.isRequired,
  updateBookingStep: PropTypes.func.isRequired,
  fetchPaymentSettings: PropTypes.func.isRequired,
  fetchPaymentSettingsById: PropTypes.func.isRequired,
  batchUpdateBooking: PropTypes.func.isRequired,
  addonGroups: addonGroupsPropTypes.isRequired,
  addMessage: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  booking: state.booking,
  bookingTerms: state.bookingTerms,
  restaurant: state.restaurant,
  addonGroups: state.addonGroups,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchBookingTerms: fetchBookingTermsAction,
      updateBooking: updateBookingAction,
      updateBookingStep: updateBookingStepAction,
      fetchPaymentSettings: fetchPaymentSettingsAction,
      fetchPaymentSettingsById: fetchPaymentSettingsByIdAction,
      holdBooking: holdBookingAction,
      batchUpdateBooking: batchUpdateBookingAction,
      holdExperiences: holdExperiencesAction,
      addMessage: addMessageAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(Details);
